import { useState, useEffect, useMemo, useCallback } from 'react';

import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ONBOARDING_INSIGHTS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useShouldExcludeUser } from '@confluence/targeting';
import { useExperimentalSiteUserPersonalization } from '@confluence/targeting/entry-points/useExperimentalSiteUserPersonalization.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const ONBOARDING_DELAY_MS = 3000;
const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';
const NEW_USER_DAYS_THRESHOLD = 28;

export const useRemixOnboarding = ({ skip }: { skip: boolean }) => {
	const [showChangeboarding, setShowChangeboarding] = useState(false);

	const { cloudId, userId } = useSessionData();
	const isLicensed = useIsLicensed();

	const { data: userPersonalizationData, loading: userTargetingLoading } =
		useExperimentalSiteUserPersonalization({
			variables: { cloudId, userId: userId as string },
			skip: !isLicensed || !userId,
		});

	const attributes = userPersonalizationData?.experimentalSiteUserPersonalization?.attributes;
	const confluenceFirstActive = attributes?.find(
		({ name }: { name: string }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;

	const firstActiveDate = confluenceFirstActive ? new Date(confluenceFirstActive) : null;

	const { shouldExclude: shouldExcludeNewUser } = useShouldExcludeUser({
		checkDate: firstActiveDate,
		daysThreshold: NEW_USER_DAYS_THRESHOLD,
	});

	const shouldSkip =
		skip ||
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		shouldExcludeNewUser ||
		userTargetingLoading;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([ONBOARDING_INSIGHTS.REMIX_PANEL_CHANGEBOARDING], shouldSkip);

	const { setOnboardingState } = useSetOnboardingState();
	const { hasRemixPanelChangeboardingBeenSeen } = deserializeState(onboardingStateData);

	const changeboardingEligible = useMemo(() => {
		const areConditionsMet =
			!shouldSkip &&
			!onboardingStateLoading &&
			!onboardingStateError &&
			!hasRemixPanelChangeboardingBeenSeen;

		return !!areConditionsMet;
	}, [
		onboardingStateLoading,
		onboardingStateError,
		hasRemixPanelChangeboardingBeenSeen,
		shouldSkip,
	]);

	useEffect(() => {
		setShowChangeboarding(false);

		if (changeboardingEligible) {
			const timer = setTimeout(() => {
				setShowChangeboarding(true);
			}, ONBOARDING_DELAY_MS);
			return () => {
				clearTimeout(timer);
			};
		}
	}, [changeboardingEligible]);

	const handleCloseChangeboarding = useCallback(() => {
		setOnboardingState({
			key: ONBOARDING_INSIGHTS.REMIX_PANEL_CHANGEBOARDING,
			value: 'true',
		});
		setShowChangeboarding(false);
	}, [setOnboardingState]);

	return { changeboardingEligible, showChangeboarding, handleCloseChangeboarding };
};
